import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import withRouter from "components/Common/withRouter"
import { isEmpty } from "lodash"
import { Col, Container, Row, Button } from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb"
import Activity from "./Activity"
import Overview from "./Overview"
import CPSComments from "./CPSComments"
import CPSOrdersTable from "./CPSOrdersTable"
import CPSShipmentsTable from "./CPSShipmentsTable"
import SubRequestsSection from "./SubRequestsSection"
import { useLoading } from "helpers/custom_hooks"
import {
  GetMyUser,
  GetCPSRequestByID,
  GetAllShipmentsForCPSRequest,
  GetAllProductionOrdersForCPSRequest,
  CancelCPSRequest,
} from "helpers/backend_helper"
import {
  IsAdminUser,
  IsCpsAdmin,
  IsCpsWarehouse,
  GetCpsRole,
  IsCPSModuleEnabled,
} from "helpers/misc_helpers"
import { useNavigate, useParams } from "react-router-dom"
import { Link } from "react-router-dom"

const CPSOverview = props => {
  const [entry, setEntry] = useState()
  const [myUser, setMyUser] = useState()
  const [shipments, setShipments] = useState([])
  const [productionOrders, setProductionOrders] = useState([])
  const [isCancelling, setIsCancelling] = useState(false)
  const [isCPSEnabled, setIsCPSEnabled] = useState(undefined)
  const [dataLoaded, setDataLoaded] = useState(false)
  const navigate = useNavigate()
  const setLoading = useLoading()
  let { id } = useParams()

  const loadData = async () => {
    setLoading(true)
    try {
      const [user, request, shipmentsData, productionOrdersData, enabled] =
        await Promise.all([
          GetMyUser(),
          GetCPSRequestByID(id),
          GetAllShipmentsForCPSRequest(id),
          GetAllProductionOrdersForCPSRequest(id),
          IsCPSModuleEnabled(),
        ])

      setIsCPSEnabled(enabled)
      setMyUser(user)
      setShipments(shipmentsData || [])
      setProductionOrders(productionOrdersData || [])
      setDataLoaded(true)

      console.log(request)

      if (request) {
        // Transform API data to match frontend expectations
        const transformedRequest = {
          ...request,
          // Map API fields to frontend expectations
          customer: {
            name: request.requestor?.name || "Unknown Customer",
            contact: request.username || "Unknown Contact",
          },
          needType:
            request.needType === "one-time" ? "one-time" : request.needType,
          status: mapStatusIndex(request.statusIndex),
          statusIndex: request.statusIndex,
          estimatedCompletion: request.desiredDeliveryDate,
          safetyStock:
            request.safetyStockMin && request.safetyStockMax
              ? {
                  min: request.safetyStockMin,
                  max: request.safetyStockMax,
                  current: Math.floor(
                    (request.safetyStockMin + request.safetyStockMax) / 2
                  ), // Mock current value
                }
              : null,
          customerSupply: request.customerSupply === 1,
          isParentRequest: request.isParentRequest === 1,
          files: [], // Not in API yet
          subRequests: request.subRequests || [], // Sub-requests from API
          // Parent request data for sub-requests
          parentRequestNumber: request.parentRequestNumber,
          parentID: request.parentID,
          parentQuantity: request.parentQuantity,
        }

        setEntry(transformedRequest)

        // If this is a sub-request, load parent request data
      } else {
        navigate("/404")
      }
    } catch (error) {
      console.log(error)
      setDataLoaded(true)
      if (error.response?.status !== 404) {
        // Only navigate to 404 if it's actually a 404, otherwise show permission error
      }
    }
    setLoading(false)
  }

  // Map status index to status string
  const mapStatusIndex = statusIndex => {
    const statusMap = {
      1: "Request Created",
      2: "CPS Offer",
      3: "Customer Decision",
      4: "Production",
      5: "Completed",
      6: "Cancelled",
      99: "Cancelled",
    }
    return statusMap[statusIndex] || "Unknown"
  }

  useEffect(() => {
    loadData()
  }, [id])

  useEffect(() => {
    if (dataLoaded && isCPSEnabled != undefined) setLoading(false)
  }, [dataLoaded, isCPSEnabled])

  const canCancelRequest = () => {
    if (!entry || !myUser) return false
    // Only Admin or Warehouse (global/CPS) and not already approved/completed/cancelled
    const isAdmin =
      IsAdminUser() || IsCpsAdmin() || GetCpsRole() === "Admin"
    const isWarehouse = IsCpsWarehouse() || GetCpsRole() === "Warehouse"
    const forbiddenStatuses = ["Production", "Completed", "Cancelled"]

    return (isAdmin || isWarehouse) && !forbiddenStatuses.includes(entry.status)
  }

  const handleCancelRequest = async () => {
    if (!entry?.id) return
    if (
      !window.confirm(
        "Do you really want to cancel this CPS request? This action cannot be undone."
      )
    ) {
      return
    }

    try {
      setIsCancelling(true)
      await CancelCPSRequest(entry.id)
      await loadData()
    } catch (error) {
      console.error("Error cancelling CPS request:", error)
      alert("Error cancelling CPS request. Please try again.")
    } finally {
      setIsCancelling(false)
    }
  }

  if (!isCPSEnabled) {
    return (
      <React.Fragment>
        <div className="page-content">
          <Container fluid>
            <Breadcrumbs title="Details" breadcrumbItem="CPS Programming" />
            <Row>
              <Col lg="12">
                <div className="text-center mb-5">
                  <h1 className="display-2 fw-medium">
                    4<i className="bx bx-buoy bx-spin text-primary display-3" />
                    3
                  </h1>
                  <h4 className="text-uppercase">
                    Sorry, you are not allowed to view this page
                  </h4>
                  <p className="text-primary">
                    This is a premium feature. To use this feature, please
                    upgrade your license!
                  </p>
                  <div className="mt-5 text-center">
                    <Link
                      className="btn btn-primary waves-effect waves-light "
                      to="/settings?tab=2"
                    >
                      Upgrade Account
                    </Link>
                    <br />
                    <Link className=" d-block mt-4" to="/dashboard">
                      <i className="bx bx-left-arrow-alt" />
                      Back to Dashboard
                    </Link>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </React.Fragment>
    )
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          {/* Render Breadcrumbs */}
          <Breadcrumbs title="Details" breadcrumbItem="CPS Programming" />

          {!isEmpty(entry) && (
            <>
              <Row>
                <Col lg="3">
                  <Overview
                    entry={entry}
                    printBtn
                    shipments={shipments}
                    productionOrders={productionOrders}
                    reload={loadData}
                    canCancel={canCancelRequest()}
                    onCancel={handleCancelRequest}
                    isCancelling={isCancelling}
                  />
                </Col>
                <Col lg="9">
                  <Row>
                    <Col lg="5">
                      <Activity
                        entry={entry}
                        myUser={myUser}
                        reload={loadData}
                      />
                    </Col>
                    <Col lg="7">
                      <CPSComments entry={entry} reload={loadData} />
                      {entry?.needType === "annual" &&
                        !entry?.parentRequestNumber && (
                          <>
                            <div className="mt-3">
                              <CPSShipmentsTable
                                entry={entry}
                                myUser={myUser}
                                shipments={shipments}
                                reload={loadData}
                              />
                            </div>
                            <div className="mt-3">
                              <CPSOrdersTable
                                entry={entry}
                                myUser={myUser}
                                productionOrders={productionOrders}
                                shipments={shipments}
                                reload={loadData}
                              />
                            </div>
                          </>
                        )}
                    </Col>
                  </Row>
                  {/* Show Sub-Requests Section if this is an annual Programming request */}
                  {entry?.type === "Programming" &&
                    entry?.needType === "annual" && (
                      <Row className="mt-3">
                        <Col lg="12">
                          <SubRequestsSection
                            parentRequest={entry}
                            myUser={myUser}
                            onSubRequestCreated={newSubRequest => {
                              // Handle new sub-request creation
                              console.log(
                                "New sub-request created:",
                                newSubRequest
                              )
                            }}
                          />
                        </Col>
                      </Row>
                    )}
                </Col>
              </Row>
            </>
          )}
        </Container>
      </div>
    </React.Fragment>
  )
}

CPSOverview.propTypes = {
  match: PropTypes.object,
}

export default withRouter(CPSOverview)
